import dayjs from 'dayjs';

import { ConsumerJobStatus } from 'dashboard/enums';
import { IConsumerJob } from 'dashboard/types';

/**
 * A numbered dev-pool env (dev0…dev20) belongs to one developer; everything else
 * — default, staging, dev, qa — is shared and read by the whole team.
 *
 * On uat and prod the only restartable env is `default`, so this correctly treats
 * the single button on those deployments as shared: restarting production's
 * consumers is exactly the click that should be hardest to make by accident.
 */
export const isDevPoolEnv = (env: string) => /^dev\d+\./.test(env);

/**
 * Whether a live consumer is actually executing this job — the only case where
 * cancelling it, or restarting its env, interrupts real work.
 *
 * Running is not enough: `stale` means the row says running but the BE could not
 * match its PID to a live consumer, so the process is already gone. Queued jobs have
 * nothing executing them by definition.
 */
export const hasLiveConsumer = ({
  stale,
  status,
}: Pick<IConsumerJob, 'stale' | 'status'>): boolean =>
  !stale && ConsumerJobStatus.RUNNING === status;

/** A job is called out once its current state has lasted longer than this. */
export const STUCK_AFTER_HOURS = 24;

/**
 * A job that has sat in the same state for more than a day: a queued one nothing
 * ever picked up, or a running one that is almost certainly wedged. Neither is
 * visible from the status alone — the list shows months-old rows as plain
 * "Queued" — so the age is what makes them findable.
 *
 * Measured from startedAt when a consumer claimed the job, and from createdAt when
 * nothing has: a queued row has no startedAt, and how long it has waited is exactly
 * the thing worth reporting for it.
 */
export const isJobStuck = ({
  createdAt,
  stale,
  startedAt,
}: Pick<IConsumerJob, 'createdAt' | 'stale' | 'startedAt'>): boolean => {
  // An orphan already carries its own flag, and it is the more specific diagnosis:
  // two chips saying "this row is wrong" would just be noise.
  if (stale) {
    return false;
  }

  const since = startedAt ?? createdAt;

  if (!since) {
    return false;
  }

  const hours = dayjs().diff(dayjs(since), 'hour');

  return hours >= STUCK_AFTER_HOURS;
};
